import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, UserPlus, Copy, QrCode, Mail, User } from 'lucide-react';
import { ResponsabileService } from '../services/apiService';
import { useToast } from "../components/ToastContext";

export const NuovoResponsabileView: React.FC = () => {
    const navigate = useNavigate();
    const { addToast } = useToast();
    const [isLoading, setIsLoading] = useState(false);

    // Form dati
    const [formData, setFormData] = useState({ name: '', surname: '', email: '' });
    const [inviteResult, setInviteResult] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            // Il metodo dedicato gestisce già ruolo e tipo di ritorno
            const response = await ResponsabileService.invite(formData);

            if (response.success) {
                // Il link di invito arriva nel campo data
                setInviteResult(response.data);
                addToast("Responsabile creato e invito generato!", "success");
            } else {
                addToast(response.message || "Errore durante la creazione", "error");
            }
        } catch (error: any) {
            const msg = error.response?.data?.message || "Errore di connessione al server";
            addToast(msg, "error");
        } finally {
            setIsLoading(false);
        }
    };

    const copyToClipboard = () => {
        if (inviteResult) {
            navigator.clipboard.writeText(inviteResult);
            addToast("Link copiato negli appunti!", "success");
        }
    };

    if (inviteResult) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center border border-gray-200 animate-in zoom-in duration-300">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 text-green-600">
                        <UserPlus size={32}/>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Invito Pronto!</h2>
                    <p className="text-gray-600 mb-6 text-sm">
                        Abbiamo pre-registrato <strong>{formData.name} {formData.surname}</strong>.<br/>
                        Condividi questo link per permettergli di impostare la password.
                    </p>

                    {/* Placeholder QR Code */}
                    <div className="bg-white p-4 rounded-xl w-48 h-48 mx-auto mb-6 flex items-center justify-center border-2 border-dashed border-gray-300">
                        <div className="text-center">
                            <QrCode className="mx-auto text-gray-300 mb-2" size={48} />
                            <span className="text-xs text-gray-400">QR Code (Opzionale)</span>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 bg-gray-50 p-3 rounded-lg mb-6 border border-gray-200">
                        <input
                            type="text"
                            readOnly
                            value={inviteResult}
                            className="bg-transparent flex-1 text-xs text-blue-600 font-mono outline-none truncate select-all"
                        />
                        <button onClick={copyToClipboard} className="text-gray-500 hover:text-gray-800 font-bold text-xs flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-200 transition-colors">
                            <Copy size={14}/> Copia
                        </button>
                    </div>

                    <div className="flex gap-3">
                        <button
                            onClick={() => {
                                setInviteResult(null);
                                setFormData({ name: '', surname: '', email: '' });
                            }}
                            className="flex-1 py-3 text-gray-600 hover:bg-gray-100 rounded-xl font-medium transition-colors"
                        >
                            Crea un altro
                        </button>
                        <button
                            onClick={() => navigate('/responsabili')}
                            className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition-colors"
                        >
                            Lista Responsabili
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="px-4 lg:px-6 py-6 w-full max-w-2xl mx-auto space-y-6">
            <div className="flex items-center gap-2 text-gray-500 hover:text-gray-900 cursor-pointer w-fit transition-colors" onClick={() => navigate('/responsabili')}>
                <ChevronLeft size={20}/> Torna indietro
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-200">
                <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center">
                        <UserPlus size={24}/>
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Invita Responsabile</h1>
                        <p className="text-sm text-gray-500">Crea un nuovo profilo manager e genera il link di attivazione.</p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                            <div className="relative">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18}/>
                                <input
                                    required
                                    type="text"
                                    className="w-full pl-10 p-3 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                    placeholder="Mario"
                                    value={formData.name}
                                    onChange={e => setFormData({...formData, name: e.target.value})}
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Cognome</label>
                            <input
                                required
                                type="text"
                                className="w-full p-3 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                placeholder="Rossi"
                                value={formData.surname}
                                onChange={e => setFormData({...formData, surname: e.target.value})}
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email Aziendale</label>
                        <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18}/>
                            <input
                                required
                                type="email"
                                className="w-full pl-10 p-3 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                placeholder="mario.rossi@azienda.it"
                                value={formData.email}
                                onChange={e => setFormData({...formData, email: e.target.value})}
                            />
                        </div>
                    </div>

                    <div className="pt-4">
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 flex justify-center items-center gap-2 disabled:opacity-70 disabled:cursor-wait"
                        >
                            {isLoading ? (
                                <>
                                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    Generazione in corso...
                                </>
                            ) : (
                                'Crea e Genera Invito'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default NuovoResponsabileView;